import {useEffect} from 'react'

import {fetchProjectWithAllParentsData} from '../actions/projects'
import BuildPath from '../components/common/BuildPath/BuildPath'
import type {BuildPathOwnProps} from '../components/common/BuildPath/BuildPath.types'
import {useProjectOrBuildTypeNodeFromUrl} from '../hooks/routes'
import {getBuildType, getPathBuildType, getFullPath, getPathBuildTypeArg} from '../selectors'
import {restApi} from '../services/rest'
import {getProjectBuildTypeFilter, toBuildId, ROOT_PROJECT_ID, ROOT_PROJECT_NAME} from '../types'
import type {BuildId, BuildTypeId, ProjectId, PathItem} from '../types'

import {useAppDispatch, useAppSelector, useAppStore} from 'src/hooks/react-redux'

type OwnProps = BuildPathOwnProps & {
  buildId?: string | BuildId
  // string for API
  projectId?: ProjectId | null
  buildTypeId?: BuildTypeId | null
  path?: ReadonlyArray<PathItem>
}

export const useLoadEntityWithAllParents = ({
  buildTypeId,
  projectId,
  skip,
}: {
  projectId?: ProjectId | null
  buildTypeId?: BuildTypeId | null
  skip?: boolean
}) => {
  const dispatch = useAppDispatch()
  const [fetchSingleBuildTypeData] =
    restApi.endpoints.getPathBuildTypeNormalized.useLazyQuerySubscription()
  const {getState} = useAppStore()
  useEffect(() => {
    if (skip) {
      return undefined
    }
    let isUnsubscribed = false
    let unsubscribe = () => {
      isUnsubscribed = true
    }
    ;(async () => {
      const state = getState()
      let projectIdToLoad = projectId

      if (buildTypeId != null) {
        let buildType = getBuildType(state, buildTypeId)

        if (buildType == null) {
          const arg = getPathBuildTypeArg(buildTypeId)
          await fetchSingleBuildTypeData(arg, true)
          buildType =
            getBuildType(getState(), buildTypeId) ?? getPathBuildType(getState(), buildTypeId)
        }

        if (buildType != null && projectIdToLoad == null) {
          projectIdToLoad = buildType.projectId
        }
      }

      if (projectIdToLoad != null && !isUnsubscribed) {
        unsubscribe = dispatch(fetchProjectWithAllParentsData(projectIdToLoad)).unsubscribe
      }
    })()
    return () => unsubscribe()
  }, [buildTypeId, dispatch, fetchSingleBuildTypeData, projectId, getState, skip])
}

export default function EntityPath({
  projectId,
  buildTypeId,
  buildId,
  pipelineId,
  jobId,
  batchId,
  path,
  ...restProps
}: OwnProps) {
  const fullPath = useAppSelector(
    state =>
      path ??
      getFullPath(
        state,
        getProjectBuildTypeFilter({
          projectId,
          buildTypeId,
          pipelineId,
          jobId,
          batchId,
        }),
      ),
  )

  useLoadEntityWithAllParents({
    projectId,
    buildTypeId,
  })

  const projectOrBuildTypeNode = useProjectOrBuildTypeNodeFromUrl()
  const buildIdProperlyTyped = typeof buildId === 'string' ? toBuildId(buildId) : buildId

  const isOnlyRootProject =
    projectId === ROOT_PROJECT_ID && !buildTypeId && !pipelineId && !jobId && !batchId && !path

  if (isOnlyRootProject && (!fullPath || fullPath.length === 0)) {
    return <span>{ROOT_PROJECT_NAME}</span>
  }
  return (
    <BuildPath
      withLeafStatusIcon
      projectOrBuildTypeNode={projectOrBuildTypeNode}
      buildId={buildIdProperlyTyped}
      pipelineId={pipelineId}
      jobId={jobId}
      batchId={batchId}
      {...restProps}
      path={fullPath}
    />
  )
}
